/**
 * ChapterListItem
 *
 * Individual chapter item with drag handle, rename, and delete functionality.
 */

import { useState, useRef, useEffect } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { GripVertical, Pencil, Trash2, Check, X } from 'lucide-react'
import type { ChapterRecord } from '@/types/writingStudio'

interface ChapterListItemProps {
  chapter: ChapterRecord
  isSelected: boolean
  onSelect: () => void
  onRename: (newTitle: string) => Promise<void>
  onDelete: () => Promise<void>
  canDelete: boolean
  disabled?: boolean
}

export function ChapterListItem({
  chapter,
  isSelected,
  onSelect,
  onRename,
  onDelete,
  canDelete,
  disabled = false,
}: ChapterListItemProps) {
  const [isEditing, setIsEditing] = useState(false)
  const [editTitle, setEditTitle] = useState(chapter.title)
  const [isDeleting, setIsDeleting] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: chapter.id, disabled: disabled || isEditing })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [isEditing])

  const handleStartEdit = (e: React.MouseEvent) => {
    e.stopPropagation()
    setEditTitle(chapter.title)
    setIsEditing(true)
  }

  const handleSaveEdit = async () => {
    const trimmed = editTitle.trim()
    if (trimmed && trimmed !== chapter.title) {
      await onRename(trimmed)
    }
    setIsEditing(false)
  }

  const handleCancelEdit = () => {
    setEditTitle(chapter.title)
    setIsEditing(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit()
    } else if (e.key === 'Escape') {
      handleCancelEdit()
    }
  }

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!canDelete) return

    setIsDeleting(true)
    try {
      await onDelete()
    } finally {
      setIsDeleting(false)
    }
  }

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`
        group flex items-center gap-2 px-2 py-2.5 rounded-[var(--radius-kid-sm)] cursor-pointer
        transition-colors
        ${isDragging ? 'opacity-50 bg-[var(--color-kids-surface-alt)]' : ''}
        ${isSelected && !isDragging
          ? 'bg-[var(--color-kids-primary)]/10 border border-[var(--color-kids-primary)]/30'
          : 'hover:bg-[var(--color-kids-surface-alt)] border border-transparent'
        }
      `}
      onClick={!isEditing ? onSelect : undefined}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        className="flex-shrink-0 p-1 text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] cursor-grab active:cursor-grabbing touch-none"
        onClick={(e) => e.stopPropagation()}
      >
        <GripVertical className="w-4 h-4" />
      </button>

      {/* Chapter content */}
      <div className="flex-1 min-w-0">
        {isEditing ? (
          <div className="flex items-center gap-1">
            <input
              ref={inputRef}
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              onKeyDown={handleKeyDown}
              onBlur={handleSaveEdit}
              className="flex-1 px-2 py-1 text-sm bg-white border border-[var(--color-kids-border)] rounded-[var(--radius-kid-sm)] outline-none focus:border-[var(--color-kids-primary)]"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleSaveEdit()
              }}
              className="p-1 text-[var(--color-kids-success)] hover:bg-[var(--color-kids-success)]/10 rounded"
            >
              <Check className="w-4 h-4" />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                handleCancelEdit()
              }}
              className="p-1 text-[var(--color-kids-text-muted)] hover:bg-[var(--color-kids-surface-alt)] rounded"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <>
            <p className={`text-sm font-medium truncate ${
              isSelected ? 'text-[var(--color-kids-primary)]' : 'text-[var(--color-kids-text)]'
            }`}>
              {chapter.title}
            </p>
            <p className="text-xs text-[var(--color-kids-text-muted)]">
              {chapter.wordCount} words
            </p>
          </>
        )}
      </div>

      {/* Actions (visible on hover or when selected) */}
      {!isEditing && (
        <div className={`flex items-center gap-1 ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
          <button
            onClick={handleStartEdit}
            disabled={disabled}
            className="p-1.5 text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-text)] hover:bg-[var(--color-kids-surface-alt)] rounded transition-colors"
            title="Rename chapter"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={disabled || isDeleting}
              className="p-1.5 text-[var(--color-kids-text-muted)] hover:text-[var(--color-kids-danger)] hover:bg-[var(--color-kids-danger)]/10 rounded transition-colors disabled:opacity-50"
              title="Delete chapter"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
